"use client";

import { PauseIcon, PlayIcon } from "@heroicons/react/24/solid";
import { FC, ReactNode, useCallback, useState } from "react";
import { AbsoluteFill, Video } from "remotion";
import { useMediaQuery } from "usehooks-ts";
import Spinner from "./Spinner";

export const PREVIEW_FPS = 30;

// App-standard play/pause glyph (mirrors NLBufferingPlayer) so these
// single-clip preview players read the same as the editor/result players.
export const renderPreviewPlayPauseButton = ({
  playing,
}: {
  playing: boolean;
}) =>
  playing ? (
    <PauseIcon className="w-6 h-6 text-white" />
  ) : (
    <PlayIcon className="w-6 h-6 text-white" />
  );

// Shared control behavior for the bare preview players: controls reveal on
// hover (desktop) and stay pinned on touch, with the app's play/pause glyph
// and volume controls. Spread `playerControlProps` onto the `<Player>` and
// `wrapperHandlers` onto its container.
export const usePreviewPlayerControls = () => {
  const isTouch = useMediaQuery("(pointer: coarse)");
  const [showControls, setShowControls] = useState(false);
  const onMouseEnter = useCallback(() => setShowControls(true), []);
  const onMouseLeave = useCallback(() => setShowControls(false), []);
  const onTouchStart = useCallback(() => setShowControls(true), []);

  return {
    isTouch,
    wrapperHandlers: { onMouseEnter, onMouseLeave, onTouchStart },
    playerControlProps: {
      controls: isTouch || showControls,
      alwaysShowControls: isTouch,
      clickToPlay: true,
      showVolumeControls: true,
      renderPlayPauseButton: renderPreviewPlayPauseButton,
    },
  };
};

// Bare composition: just the source video filling the canvas. The Remotion
// player's native controls are hidden (no seekbar, no time); scrubbing and
// time live in the dialog's two custom bars instead.
export const SourcePreviewComposition: FC<{ src: string }> = ({ src }) => (
  <AbsoluteFill className="bg-black">
    <Video src={src} pauseWhenBuffering />
  </AbsoluteFill>
);

// The single overlay style shared across the whole moment surface: a dark veil
// with its content (a spinner while something loads / buffers) centered directly
// on it — no badge chrome. `rounded-xl` (12px) matches the card / player-box
// corners and rounds intrinsically, so the veil survives the deck's 3D
// transform. `text-white` colors the spinner (it strokes currentColor).
export const ThumbBadgeOverlay: FC<{ children: ReactNode }> = ({
  children,
}) => (
  <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl bg-slate-950/30 text-white">
    {children}
  </div>
);

// Poster (buffering) + initial load: a spinner centered on the shared dim veil,
// so the loading state matches the rest of the moment surface.
export const renderPreviewPoster = () => (
  <ThumbBadgeOverlay>
    <Spinner className="h-8 w-8" />
  </ThumbBadgeOverlay>
);

export const renderPreviewLoading = renderPreviewPoster;
